import { useEffect, useState } from "react";
import { Cog } from "lucide-react";

// Shown in turn while the page waits. Our servers can take up to a minute to wake, so there are plenty.
const LINES = [
  "Warming up the engines…",
  "Brewing a fresh pot of coffee…",
  "Sharpening the pencils…",
  "Dusting off the blueprints…",
  "Untangling a few spreadsheets…",
  "Lining up the dominoes…",
  "Oiling the gears…",
  "Counting the hours you'll get back…",
  "Rounding up the team…",
  "Teaching the robots some manners…",
  "Filing the paperwork, so you don't have to…",
  "Putting the finishing touches on…",
];
const LINE_MS = 2600;
const PATIENT_MS = 12_000;

/**
 * The site's one loading state: two turning gears and a playful line that changes every few seconds. After a while
 * it adds a reassurance that the first load can take up to a minute. `label` says what is being loaded.
 */
export default function Loader({ label = "Getting things ready", compact = false }: { label?: string; compact?: boolean }) {
  const [line, setLine] = useState(0);
  const [patient, setPatient] = useState(false);

  useEffect(() => {
    const turn = window.setInterval(() => setLine((i) => (i + 1) % LINES.length), LINE_MS);
    const wait = window.setTimeout(() => setPatient(true), PATIENT_MS);
    return () => { window.clearInterval(turn); window.clearTimeout(wait); };
  }, []);

  return (
    <div role="status" aria-live="polite"
         className={`flex flex-col items-center text-center ${compact ? "gap-2 py-6" : "gap-4 py-16"}`}>
      <span aria-hidden="true" className={`relative ${compact ? "h-10 w-12" : "h-16 w-20"}`}>
        <Cog className={`loader-spin absolute left-0 top-0 text-primary ${compact ? "h-8 w-8" : "h-12 w-12"}`} />
        <Cog className={`loader-spin-back absolute bottom-0 right-0 text-accentPurple ${compact ? "h-5 w-5" : "h-8 w-8"}`} />
      </span>
      <span className="sr-only">{label}</span>
      <p aria-hidden="true" className={`font-semibold text-text-primary ${compact ? "text-base" : "text-lg"}`}>{label}</p>
      <p key={line} aria-hidden="true" className={`catalog-enter text-text-secondary ${compact ? "text-sm" : "text-base"}`}>
        {LINES[line]}
      </p>
      {patient && (
        <p className="catalog-enter max-w-md text-sm text-text-muted">
          The first visit in a while can take up to a minute while everything wakes up. Thanks for waiting.
        </p>
      )}
    </div>
  );
}
